/**
 * utils/inputConstraints.ts
 * Single source of truth for strict, type-as-you-go input constraints.
 *
 * Each rule carries a `block` regex matching the DISALLOWED characters for a
 * field (used with String.replace to strip them before they reach state) and a
 * human `message` shown in a toast when something is stripped. No special
 * characters (@ # + ! $ % …) are permitted by any rule below.
 *
 * Usage (React):
 *   const guard = useInputGuard();
 *   <TextInput onChangeText={(v) => setTitle(guard(v, INPUT_RULES.TITLE))} />
 *
 * Usage (pure, no toast):
 *   const { value } = sanitize(raw, INPUT_RULES.TITLE);
 */
import { useRef, useCallback } from 'react';
import { showToast } from '../components/toastComponent';

export type Constraint = {
  /** Matches the characters that are NOT allowed (stripped on input). */
  block: RegExp;
  /** Toast detail shown when a disallowed character is blocked. */
  message: string;
};

/** Named constraints, keyed by field intent. Reuse across forms. */
export const INPUT_RULES = {
  /** Letters, spaces and commas only — no digits, no special chars. */
  TITLE: {
    block: /[^A-Za-z, ]/g,
    message: 'Title allows letters, spaces and commas only — no numbers or special characters.',
  },
  /** Letters, spaces and commas only (place names). */
  LOCATIONS: {
    block: /[^A-Za-z, ]/g,
    message: 'Locations allow letters, spaces and commas only — no special characters.',
  },
  /** Letters, digits, spaces and commas — comma-separated skill lists. */
  SKILLS: {
    block: /[^A-Za-z0-9, ]/g,
    message: 'Skills allow letters, numbers, spaces and commas only.',
  },
  /** Letters, digits, whitespace (incl. newlines) and commas — pasted JD text. */
  JD: {
    block: /[^A-Za-z0-9,\s]/g,
    message: 'The job description allows letters, numbers, spaces and commas only — no special characters.',
  },
  /** Digits and spaces only — free-form numeric identifiers. */
  ID_NUMERIC: {
    block: /[^0-9 ]/g,
    message: 'This ID allows numbers and spaces only.',
  },
  /** Digits only — counts, years, quantities. */
  NUMBER: {
    block: /[^0-9]/g,
    message: 'This field accepts numbers only.',
  },
} as const satisfies Record<string, Constraint>;

export type InputRuleKey = keyof typeof INPUT_RULES;

/**
 * Strip disallowed characters from `raw` per `rule`. Pure — no side effects.
 * Returns the cleaned value and whether anything was removed.
 */
export function sanitize(raw: string, rule: Constraint): { value: string; blocked: boolean } {
  const value = raw.replace(rule.block, '');
  return { value, blocked: value !== raw };
}

/**
 * React hook returning a `guard(raw, rule)` function that sanitizes input and
 * fires a throttled error toast whenever a disallowed character is blocked.
 * The throttle collapses a held key or a bad paste into a single toast.
 *
 * @param throttleMs minimum gap between "blocked" toasts (default 1600ms)
 */
export function useInputGuard(throttleMs = 1600): (raw: string, rule: Constraint) => string {
  const last = useRef(0);
  return useCallback(
    (raw: string, rule: Constraint): string => {
      const { value, blocked } = sanitize(raw, rule);
      if (blocked) {
        const now = Date.now();
        if (now - last.current > throttleMs) {
          last.current = now;
          showToast('error', 'Character not allowed', rule.message);
        }
      }
      return value;
    },
    [throttleMs],
  );
}
